/**
 * Decisions Page (FLOW-572, review controls added in FLOW-667)
 *
 * Keyword + filter search over a project's decisions. Capture happens
 * elsewhere — `DecisionQuickCapture` (FLOW-570), breakdown extraction, or
 * the MCP `propose_decision` tool — but review happens here: a row still
 * `proposed` carries Confirm / Dismiss, running the same conflict check as
 * `CapturedDecisionsPanel` via `useDecisionReview`.
 *
 * That is the only home an agent-proposed (`mcp_agent`) decision has. The
 * captured-decisions panel loads through `list_for_intake`, which filters to
 * `source_type = 'plan_conversation'` with an intake to belong to, so a row
 * from `propose_decision` never reaches it.
 */

import {
  Component,
  For,
  Show,
  createEffect,
  createMemo,
  createResource,
  createSignal,
  on,
} from "solid-js";
import { useSearchParams } from "@solidjs/router";
import { SearchInput, Spinner, EmptyState } from "../components/ui";
import { ConflictWarningsPanel } from "../components/decisions/ConflictWarningsPanel";
import { DecisionConflictWarnings } from "../components/decisions/DecisionConflictWarnings";
import {
  confirmDecision,
  dismissDecision,
  getDecision,
  searchDecisions,
} from "../api/decisions";
import type {
  ConflictResolution,
  Decision,
  DecisionCategory,
  DecisionConflictWarning,
  DecisionId,
  DecisionStatus,
} from "../api/types";
import { useProjectId } from "../contexts/ProjectContext";
import { useDecisionReview } from "../hooks/useDecisionReview";
import { useCanConfirmDecisions } from "../hooks/useCanConfirmDecisions";
import { describeAiError, surfaceQuotaExhausted } from "../utils/aiError";
import {
  DECISION_CATEGORY_BADGE_COLOR,
  DECISION_CATEGORY_OPTIONS,
  DECISION_STATUS_OPTIONS,
  decisionCategoryLabel,
  decisionStatusColor,
  decisionStatusLabel,
} from "../utils/decisionDisplay";

// api/decisions.ts splices this straight into the request path
// (`/projects/{pid}/decisions/{id}`); a `?focus=` that isn't a well-formed
// id would otherwise ride along unvalidated. Not exploitable — the backend
// authorizes `get_decision` against whatever project the resolved path
// lands on — but it should fail closed here rather than fire a confusing
// request. There's no existing UUID predicate under `src/utils/` to reuse.
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function chipClasses(active: boolean): string {
  return (
    "text-xs rounded-full px-2.5 py-1 border transition-colors focus-ring " +
    (active
      ? "bg-ink border-ink text-paper font-medium"
      : "bg-surface border-line text-ink-2 hover:bg-sunken")
  );
}

const DecisionRow: Component<{
  decision: Decision;
  /** Mirrors the backend's gate on confirmation — see `useCanConfirmDecisions` */
  canConfirm: boolean;
  confirmDisabled: boolean;
  onConfirmClick: () => void;
  onCancelClick: () => void;
  onDismissClick: () => void;
  showWarnings: boolean;
  warnings: DecisionConflictWarning[];
  choices: Record<string, ConflictResolution | undefined>;
  onChoose: (warningId: string, outcome: ConflictResolution) => void;
  busy: boolean;
  /** Pre-expand a row pinned from the command palette's `?focus=` deep link */
  startExpanded?: boolean;
}> = (props) => {
  // Read once at creation — correct for a per-row instance, not a
  // reactivity break: `startExpanded` never changes after a row mounts.
  const [expanded, setExpanded] = createSignal(!!props.startExpanded);

  return (
    <li>
      <div class="flex items-start gap-2 px-4 py-3 hover:bg-sunken transition-colors">
        <button
          type="button"
          class="flex-1 min-w-0 text-left flex items-start gap-2"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded()}
        >
          <svg
            class={
              "w-4 h-4 mt-0.5 text-ink-3 flex-shrink-0 transition-transform " +
              (expanded() ? "rotate-90" : "")
            }
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 5l7 7-7 7"
            />
          </svg>
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2 flex-wrap">
              <span class="text-sm font-medium text-ink">
                {props.decision.title}
              </span>
              <span
                class={
                  "inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border " +
                  DECISION_CATEGORY_BADGE_COLOR
                }
              >
                {decisionCategoryLabel(props.decision.category)}
              </span>
              <span
                class={
                  "inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border " +
                  decisionStatusColor(props.decision.status)
                }
              >
                {decisionStatusLabel(props.decision.status)}
              </span>
            </div>
            <Show when={expanded()}>
              <p class="mt-2 text-sm text-ink-2 whitespace-pre-wrap">
                {props.decision.rationale}
              </p>
              <Show when={props.decision.tags.length > 0}>
                <div class="mt-2 flex flex-wrap gap-1">
                  <For each={props.decision.tags}>
                    {(tag) => (
                      <span class="text-xs text-ink-2 bg-sunken border border-line rounded px-1.5 py-0.5">
                        {tag}
                      </span>
                    )}
                  </For>
                </div>
              </Show>
            </Show>
          </div>
        </button>

        <Show when={props.decision.status === "proposed"}>
          <div class="flex shrink-0 items-center gap-2">
            <Show when={props.showWarnings}>
              <button
                type="button"
                aria-label={`Cancel conflict review for "${props.decision.title}"`}
                disabled={props.busy}
                onClick={props.onCancelClick}
                class="rounded-full bg-sunken px-2.5 py-1 text-xs font-medium text-ink-2 hover:bg-line hover:text-ink focus-ring disabled:cursor-not-allowed disabled:opacity-50"
              >
                Cancel
              </button>
            </Show>
            <Show when={props.canConfirm}>
              <button
                type="button"
                aria-label={`Confirm "${props.decision.title}"`}
                disabled={props.confirmDisabled}
                onClick={props.onConfirmClick}
                class="rounded-full bg-signal-green-tint px-2.5 py-1 text-xs font-medium text-signal-green hover:bg-signal-green/15 focus-ring disabled:cursor-not-allowed disabled:opacity-50"
              >
                Confirm
              </button>
            </Show>
            <button
              type="button"
              aria-label={`Dismiss "${props.decision.title}"`}
              onClick={props.onDismissClick}
              class="rounded-full bg-signal-red-tint px-2.5 py-1 text-xs font-medium text-signal-red hover:bg-signal-red/15 focus-ring"
            >
              Dismiss
            </button>
          </div>
        </Show>
      </div>

      <Show when={props.showWarnings}>
        <div class="px-4 pb-3">
          <DecisionConflictWarnings
            warnings={props.warnings}
            choices={props.choices}
            onChoose={props.onChoose}
            allowEdited={false}
            busy={props.busy}
          />
        </div>
      </Show>
    </li>
  );
};

export const DecisionsPage: Component = () => {
  const projectId = useProjectId();

  const [query, setQuery] = createSignal("");
  const [status, setStatus] = createSignal<DecisionStatus | "">("");
  const [category, setCategory] = createSignal<DecisionCategory | "">("");
  const [reviewError, setReviewError] = createSignal<string | null>(null);

  const [params, setParams] = useSearchParams<{ focus?: string }>();
  const [focused, { mutate: mutateFocused }] = createResource(
    () => {
      const pid = projectId();
      const id = params.focus;
      return pid && id && UUID_RE.test(id) ? ([pid, id] as const) : false;
    },
    ([pid, id]) => getDecision(pid, id),
  );
  // Same reasoning as `decisions` below: an errored resource read rethrows,
  // which would take the whole page down over a stale or bad ?focus= id.
  // Also re-checks `params.focus` and `.loading` itself: createResource
  // keeps its last value once its source goes falsy or while a new fetch is
  // in flight (it does not reset on unload or mid-refetch), so without this
  // a cleared focus — or a focus that just moved to a *different* id — would
  // show the previous decision's stale row instead of nothing.
  const focusedDecision = createMemo(() => {
    if (!params.focus || focused.loading) return undefined;
    return focused.state === "errored" ? undefined : focused();
  });
  const clearFocus = () => setParams({ focus: undefined });

  const [response, { refetch, mutate }] = createResource(
    () => [projectId(), query(), status(), category()] as const,
    ([pid, q, statusFilter, categoryFilter]) =>
      searchDecisions(pid, {
        q: q.trim() || undefined,
        status: statusFilter || undefined,
        category: categoryFilter || undefined,
      }),
  );

  // Guard on `response.state`, not `response()` directly: the resource
  // accessor throws the stored error when read in the "errored" state (and
  // `.latest` does too, once there's no pending refetch), which would abort
  // this memo mid-flight and leave the error Show below unrendered.
  // Filters out `focusedDecision()?.id`, not raw `params.focus`: the two
  // only diverge while the pin is loading or has errored, and in both cases
  // there is no pinned row to duplicate against, so the list is the only
  // place left the decision can be seen. Filtering on the raw param would
  // hide it from the list too — invisible everywhere, not just off by one
  // render tick.
  const decisions = createMemo(() =>
    response.state === "errored"
      ? []
      : (response()?.decisions ?? []).filter(
          (d) => d.id !== focusedDecision()?.id,
        ),
  );
  const hasFilters = createMemo(
    () => query().trim() !== "" || status() !== "" || category() !== "",
  );

  // Confirm and dismiss patch the loaded page rather than refetching it: the
  // search itself hasn't changed, and a refetch would blank the list behind a
  // spinner mid-review. A confirmed row stays put with an Active badge — the
  // feedback that the confirm landed — until the next search runs.
  const canConfirm = useCanConfirmDecisions(projectId);

  const review = useDecisionReview(projectId, {
    onConfirm: async (id: DecisionId) => {
      setReviewError(null);
      try {
        const updated = await confirmDecision(projectId(), id);
        mutate(
          (prev) =>
            prev && {
              ...prev,
              decisions: prev.decisions.map((d) => (d.id === id ? updated : d)),
            },
        );
        if (id === params.focus) mutateFocused(updated);
        return true;
      } catch (err) {
        surfaceQuotaExhausted(err);
        setReviewError(describeAiError(err));
        return false;
      }
    },
    onDismiss: (id: DecisionId) => {
      setReviewError(null);
      void dismissDecision(projectId(), id)
        .then(() => {
          mutate(
            (prev) =>
              prev && {
                ...prev,
                decisions: prev.decisions.filter((d) => d.id !== id),
                count: Math.max(0, prev.count - 1),
              },
          );
          if (id === params.focus) clearFocus();
        })
        .catch((err) => {
          surfaceQuotaExhausted(err);
          setReviewError(describeAiError(err));
        });
    },
  });

  const toggleStatus = (value: DecisionStatus) => {
    clearFocus();
    setStatus((v) => (v === value ? "" : value));
  };
  const toggleCategory = (value: DecisionCategory) => {
    clearFocus();
    setCategory((v) => (v === value ? "" : value));
  };
  const onSearch = (value: string) => {
    clearFocus();
    setQuery(value);
  };

  let pinnedRef: HTMLDivElement | undefined;
  // Keyed on the id, not the whole memo: `focusedDecision()` is a new object
  // every time `mutateFocused` patches it (e.g. after Confirm), and without
  // `on` this effect would re-fire on that content update too, yanking the
  // pin back into view under a user who has already scrolled past it.
  createEffect(
    on(
      () => focusedDecision()?.id,
      (id) => {
        if (id) pinnedRef?.scrollIntoView({ block: "nearest" });
      },
    ),
  );

  return (
    <div class="h-full overflow-auto">
      {/* Header */}
      <div class="px-4 py-4 border-b border-line bg-surface">
        <h1 class="text-lg font-semibold text-ink">Decisions</h1>
        <p class="text-sm text-ink-3 mt-0.5 mb-3">
          Search decisions captured for this project
        </p>

        <SearchInput
          value={query()}
          onSearch={onSearch}
          debounceMs={200}
          placeholder="Search by title, rationale, or tag…"
          class="max-w-md"
        />

        <div class="flex flex-wrap items-center gap-1.5 mt-3">
          <For each={DECISION_STATUS_OPTIONS}>
            {(opt) => (
              <button
                type="button"
                onClick={() => toggleStatus(opt.value)}
                aria-pressed={status() === opt.value}
                class={chipClasses(status() === opt.value)}
              >
                {opt.label}
              </button>
            )}
          </For>
          <span class="w-px self-stretch bg-line mx-1" aria-hidden="true" />
          <For each={DECISION_CATEGORY_OPTIONS}>
            {(opt) => (
              <button
                type="button"
                onClick={() => toggleCategory(opt.value)}
                aria-pressed={category() === opt.value}
                class={chipClasses(category() === opt.value)}
              >
                {opt.label}
              </button>
            )}
          </For>
        </div>
      </div>

      {/*
        Keyed on the id (a primitive, `keyed`) so switching to a *different*
        focused decision remounts DecisionRow and its `startExpanded` prop
        re-applies — an unkeyed Show only re-runs children on a
        falsy<->truthy flip, so a second focus without an intervening clear
        would otherwise reuse the first row (and its since-collapsed
        state). The nested unkeyed Show inside re-establishes an accessor
        for that same id's lifetime, so a same-id update (`mutateFocused`
        after a confirm) still flows through without remounting the row.
      */}
      <Show when={focusedDecision()?.id} keyed>
        {(_id) => (
          <Show when={focusedDecision()}>
            {(decision) => (
              <div ref={pinnedRef} class="mx-4 mt-4">
                <div class="flex items-center justify-between mb-1.5">
                  <h2 class="text-xs font-medium uppercase tracking-wide text-ink-3">
                    From search
                  </h2>
                  <button
                    type="button"
                    class="text-xs font-medium text-ink-2 hover:text-ink focus-ring"
                    onClick={clearFocus}
                  >
                    Clear
                  </button>
                </div>
                <ul class="bg-surface rounded border border-line overflow-hidden">
                  <DecisionRow
                    decision={decision()}
                    canConfirm={canConfirm()}
                    confirmDisabled={review.confirmDisabled(decision().id)}
                    onConfirmClick={() => review.confirm(decision().id)}
                    onCancelClick={review.cancel}
                    onDismissClick={() => review.dismiss(decision().id)}
                    showWarnings={review.showingWarnings(decision().id)}
                    warnings={review.warnings()}
                    choices={review.choices()}
                    onChoose={review.choose}
                    busy={review.applying()}
                    startExpanded
                  />
                </ul>
              </div>
            )}
          </Show>
        )}
      </Show>

      <ConflictWarningsPanel projectId={projectId} />

      {/* Content */}
      <div class="py-4">
        <Show when={reviewError() ?? review.applyError()}>
          {(message) => (
            <div
              role="alert"
              class="mx-4 mb-4 flex items-start justify-between gap-4 rounded border border-signal-red/25 bg-signal-red-tint px-4 py-3"
            >
              <p class="text-sm text-signal-red">{message()}</p>
              <button
                type="button"
                class="shrink-0 text-sm font-medium text-signal-red hover:text-ink"
                onClick={() => {
                  setReviewError(null);
                  review.clearApplyError();
                }}
              >
                Dismiss
              </button>
            </div>
          )}
        </Show>

        <Show when={response.loading}>
          <div class="flex justify-center py-12">
            <Spinner size="lg" />
          </div>
        </Show>

        <Show when={response.error}>
          <div class="mx-4 bg-signal-red-tint border border-signal-red/25 rounded p-4 text-signal-red text-sm">
            Failed to load decisions.{" "}
            <button
              class="underline hover:no-underline"
              onClick={() => refetch()}
            >
              Try again
            </button>
          </div>
        </Show>

        <Show when={!response.loading && !response.error}>
          <Show
            when={decisions().length > 0}
            fallback={
              // A project whose only decision is the one pinned above isn't
              // actually empty — claiming otherwise here would contradict
              // the pinned row a few lines up.
              <Show when={!focusedDecision()}>
                <EmptyState
                  title={
                    hasFilters() ? "No matching decisions" : "No decisions yet"
                  }
                  description={
                    hasFilters()
                      ? "Try a different search or clear filters."
                      : "Decisions captured from planning conversations or quick capture will show up here."
                  }
                />
              </Show>
            }
          >
            <ul class="bg-surface rounded border border-line mx-4 overflow-hidden divide-y divide-line">
              <For each={decisions()}>
                {(decision) => (
                  <DecisionRow
                    decision={decision}
                    canConfirm={canConfirm()}
                    confirmDisabled={review.confirmDisabled(decision.id)}
                    onConfirmClick={() => review.confirm(decision.id)}
                    onCancelClick={review.cancel}
                    onDismissClick={() => review.dismiss(decision.id)}
                    showWarnings={review.showingWarnings(decision.id)}
                    warnings={review.warnings()}
                    choices={review.choices()}
                    onChoose={review.choose}
                    busy={review.applying()}
                  />
                )}
              </For>
            </ul>
          </Show>
        </Show>
      </div>
    </div>
  );
};

export default DecisionsPage;
